import { useCallback, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useLocalStorage } from 'src/shared/hooks/useStorage'
import {
  CHAT_SIDEBAR_VIEWPORT_MARGIN,
  DEFAULT_CHAT_SIDEBAR_WIDTH,
  MAX_CHAT_SIDEBAR_WIDTH,
  MIN_CHAT_SIDEBAR_WIDTH,
  REQUEST_CHAT_SIDEBAR_WIDTH_KEY,
} from './consts'
import type { ChatDraftStore, LocalChatNote } from './types'
import {
  detectChatRecipient,
  type ChatStickyRecipient,
} from './utils/detectChatRecipient'
import { escapeHtml } from './utils'

const stickyRecipientKey = (publicId: string) =>
  `request-chat-sticky-recipient:${publicId}`

const clampChatSidebarWidth = (width: number) => {
  const viewportMax = Math.max(
    MIN_CHAT_SIDEBAR_WIDTH,
    window.innerWidth - CHAT_SIDEBAR_VIEWPORT_MARGIN
  )

  return Math.min(
    Math.max(width, MIN_CHAT_SIDEBAR_WIDTH),
    Math.min(MAX_CHAT_SIDEBAR_WIDTH, viewportMax)
  )
}

const notesKey = (publicId: string) => `request-chat-notes:${publicId}`
const createLocalChatNote = (
  note: Omit<LocalChatNote, 'id' | 'createdAt'>
): LocalChatNote => ({
  id:
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  createdAt: new Date().toISOString(),
  ...note,
})

/** Drop locally stored notes and sticky recipient for a request chat. */
export const resetRequestChatDraft = (publicId: string) => {
  try {
    window.localStorage.removeItem(notesKey(publicId))
    window.localStorage.removeItem(stickyRecipientKey(publicId))
  } catch (error) {
    console.error('Failed to reset request chat draft:', error)
  }
}

/** Seed a draft note before navigating to the request (dashboard → request chat). */
export const seedInitialChatNote = (
  publicId: string,
  plainText: string,
  options?: { replace?: boolean }
) => {
  const trimmed = plainText.trim()
  if (!trimmed) return

  const { recipient, nextSticky } = detectChatRecipient(trimmed, null)

  try {
    const existing = options?.replace
      ? []
      : (JSON.parse(
          window.localStorage.getItem(notesKey(publicId)) ?? '[]'
        ) as LocalChatNote[])

    if (existing.some((note) => note.plainText === trimmed)) return

    const note = createLocalChatNote({
      content: `<p>${escapeHtml(trimmed)}</p>`,
      plainText: trimmed,
      recipient,
    })

    window.localStorage.setItem(
      notesKey(publicId),
      JSON.stringify([...existing, note])
    )

    if (nextSticky) {
      window.localStorage.setItem(
        stickyRecipientKey(publicId),
        JSON.stringify(nextSticky)
      )
    }
  } catch (error) {
    console.error('Failed to seed initial chat note:', error)
  }
}

export const useRequestChatStickyRecipient = (
  publicId: string | undefined
) => {
  const storageId = publicId ?? 'unknown'
  const [stickyRecipient, setStickyRecipient] = useLocalStorage<
    ChatStickyRecipient
  >(stickyRecipientKey(storageId), null)

  return { stickyRecipient, setStickyRecipient }
}

export const useRequestChatDraftStore = (
  publicId: string | undefined
): ChatDraftStore => {
  const storageId = publicId ?? 'unknown'
  const [localNotes, setLocalNotes] = useLocalStorage<LocalChatNote[]>(
    notesKey(storageId),
    []
  )

  const addNote = (note: Omit<LocalChatNote, 'id' | 'createdAt'>) => {
    const entry = createLocalChatNote(note)
    setLocalNotes((prev) => [...prev, entry])
    return entry
  }

  const clearNotes = () => setLocalNotes([])

  return {
    notes: localNotes,
    addNote,
    clearNotes,
  }
}

export const useRequestChatSidebarResize = () => {
  const [storedWidth, setStoredWidth] = useLocalStorage(
    REQUEST_CHAT_SIDEBAR_WIDTH_KEY,
    DEFAULT_CHAT_SIDEBAR_WIDTH
  )
  const [isResizing, setIsResizing] = useState(false)

  const width = clampChatSidebarWidth(storedWidth)

  const startResize = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault()

      const startX = event.clientX
      const startWidth = width

      setIsResizing(true)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const nextWidth = clampChatSidebarWidth(
          startWidth + (startX - moveEvent.clientX)
        )
        setStoredWidth(nextWidth)
      }

      const handleMouseUp = () => {
        setIsResizing(false)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [setStoredWidth, width]
  )

  return { width, isResizing, startResize }
}
